import { ChatMessage, CHAT_URL } from './types';
import { supabase } from '@/integrations/supabase/client';

export async function streamChat(
  allMessages: ChatMessage[],
  onCreateMessage: (id: string) => void,
  onUpdateContent: (id: string, content: string) => void,
  abortSignal?: AbortSignal,
  language?: string
): Promise<void> {
  const apiMessages = allMessages.map(m => ({ role: m.role, content: m.content }));

  // Get the user's session token for authenticated requests
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const resp = await fetch(CHAT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ messages: apiMessages, language: language || 'en' }),
    signal: abortSignal,
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Request failed' }));
    if (resp.status === 401) throw new Error('Please sign in to use the financial chatbot.');
    if (resp.status === 429) throw new Error('Rate limit exceeded. Please wait a moment.');
    if (resp.status === 402) throw new Error('AI credits exhausted. Please try again later.');
    throw new Error(err.error || `Error ${resp.status}`);
  }

  if (!resp.body) throw new Error('No response body');

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let content = '';

  const assistantId = crypto.randomUUID();
  onCreateMessage(assistantId);

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIdx: number;
      while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
        let line = buffer.slice(0, newlineIdx);
        buffer = buffer.slice(newlineIdx + 1);

        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line.startsWith(':') || line.trim() === '') continue;
        if (!line.startsWith('data: ')) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === '[DONE]') return;

        try {
          const parsed = JSON.parse(jsonStr);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (delta) {
            content += delta;
            onUpdateContent(assistantId, content);
          }
        } catch {
          buffer = line + '\n' + buffer;
          break;
        }
      }
    }

    // Flush remaining buffer
    if (buffer.trim()) {
      for (const raw of buffer.split('\n')) {
        if (!raw?.startsWith('data: ')) continue;
        const jsonStr = raw.slice(6).trim();
        if (jsonStr === '[DONE]') continue;
        try {
          const parsed = JSON.parse(jsonStr);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (delta) {
            content += delta;
            onUpdateContent(assistantId, content);
          }
        } catch { /* ignore */ }
      }
    }
  } finally {
    reader.releaseLock();
  }
}